import React, { useEffect, useState } from "react";
import {
  FaFilter,
  FaTimes,
  FaSearch,
  FaUser,
  FaWallet,
  FaRupeeSign,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaGift,
  FaUserCheck,
  FaCalendarAlt,
  FaMobile,
  FaEnvelope,
  FaTag,
  FaCalendarDay
} from "react-icons/fa";
import Swal from "../../utils/swalCompat";
import { getBonusHistory } from "../../api/endpoints";
import PageHeader from "../../components/PageHeader";

// -----------------------------------------------------------------------------
// MAIN COMPONENT
// -----------------------------------------------------------------------------

const BonusHistoryList = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showFilter, setShowFilter] = useState(false);

  // Fetch bonus history
  const fetchBonusHistory = async () => {
    try {
      setLoading(true);

      // Prepare query parameters
      const params = {
        page,
        limit
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      // Date filter logic
      if (startDate && endDate) {
        // Both dates selected - range filter
        params.startDate = startDate;
        params.endDate = endDate;
      } else if (startDate && !endDate) {
        // Only start date selected
        params.startDate = startDate;
      } else if (!startDate && endDate) {
        // Only end date selected
        params.endDate = endDate;
      }

      console.log("Fetching Bonus History with params:", params);
      const response = await getBonusHistory(params);
      console.log("Bonus History Response:", response);

      // Handle response structure
      if (response.success) {
        const responseData = response.data;
        setTransactions(responseData.transactions || []);
        setTotalPages(responseData.totalPages || 1);
        setTotalItems(responseData.total || 0);

        if (responseData.page) {
          setPage(responseData.page);
        }
      } else {
        Swal.fire("Error", response.message || "Failed to fetch bonus history", "error");
      }
    } catch (error) {
      console.error("Error fetching bonus history:", error);
      Swal.fire("Error", error.response?.data?.message || "Failed to fetch bonus history", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch on page/filter change
  useEffect(() => {
    fetchBonusHistory();
  }, [search, statusFilter, startDate, endDate, page]);

  // Debounce search
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (search || statusFilter || startDate || endDate) {
        setPage(1);
      }
    }, 500);
    return () => clearTimeout(debounceTimer);
  }, [search, statusFilter, startDate, endDate]);

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      'APPROVED': { color: 'bg-success', icon: <FaCheckCircle />, text: 'Approved' },
      'PENDING': { color: 'bg-warning', icon: <FaClock />, text: 'Pending' },
      'REJECTED': { color: 'bg-danger', icon: <FaTimesCircle />, text: 'Rejected' }
    };
    const statusData = statusMap[status] || statusMap['PENDING'];
    return (
      <span className={`badge ${statusData.color} d-inline-flex align-items-center gap-1`}>
        {statusData.icon} {statusData.text}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Format date for display
  const formatDateDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Pagination handlers
  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setPage(pageNumber);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;

    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  // Clear filters
  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  const hasActiveFilters = search || statusFilter || startDate || endDate;

  // Calculate statistics
  const totalAmount = transactions.reduce((sum, t) => sum + (t.amount || 0), 0);
  const approvedCount = transactions.filter(t => t.status === 'APPROVED').length;
  const pendingCount = transactions.filter(t => t.status === 'PENDING').length;
  const rejectedCount = transactions.filter(t => t.status === 'REJECTED').length;

  return (
    <div className="lead-page">
      <PageHeader
        title="Bonus History"
        subtitle="View all bonus transactions"
      />


      <div className="card mb-4 card-light">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
          <h5 className="card-title mb-0">Bonus Transactions</h5>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            {showFilter && (
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <div className="input-group" style={{ width: '200px' }}>
                  <span className="input-group-text">
                    <FaSearch />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by user or mobile..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>

                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  style={{ width: '140px' }}
                >
                  <option value="">All Status</option>
                  <option value="APPROVED">Approved</option>
                  <option value="PENDING">Pending</option>
                  <option value="REJECTED">Rejected</option>
                </select>

                <div className="input-group" style={{ width: '160px' }}>
                  <span className="input-group-text">
                    <FaCalendarAlt />
                  </span>
                  <input
                    type="date"
                    className="form-control"
                    placeholder="Start Date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>

                <div className="input-group" style={{ width: '160px' }}>
                  <span className="input-group-text">
                    <FaCalendarDay />
                  </span>
                  <input
                    type="date"
                    className="form-control"
                    placeholder="End Date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>

                {hasActiveFilters && (
                  <button
                    className="btn btn-outline-danger"
                    onClick={clearFilters}
                    title="Clear all filters"
                  >
                    <FaTimes /> Clear
                  </button>
                )}
              </div>
            )}
            <button
              className={`btn ${showFilter ? 'btn-primary' : 'btn-dark'}`}
              onClick={() => setShowFilter(!showFilter)}
            >
              <FaFilter />
              {showFilter ? ' Hide' : ' Filter'}
            </button>
          </div>
        </div>

        <div className="card-body">
          {/* Stats and Info */}
          <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <span className="text-muted">
                Showing {transactions.length} of {totalItems} transactions
              </span>
              {hasActiveFilters && (
                <span className="badge bg-info ms-2">Filters Applied</span>
              )}
            </div>
            <div>
              <span className="text-muted">
                Page {page} of {totalPages}
              </span>
            </div>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="mb-3 d-flex gap-2 flex-wrap">
              {search && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Search: {search}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => { setSearch(""); setPage(1); }}
                    style={{ background: 'none', border: 'none' }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {statusFilter && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Status: {statusFilter}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => { setStatusFilter(""); setPage(1); }}
                    style={{ background: 'none', border: 'none' }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {startDate && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  From: {formatDateDisplay(startDate)}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => { setStartDate(""); setPage(1); }}
                    style={{ background: 'none', border: 'none' }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {endDate && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  To: {formatDateDisplay(endDate)}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => { setEndDate(""); setPage(1); }}
                    style={{ background: 'none', border: 'none' }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Table */}
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User Details</th>
                  <th>Amount</th>
                  <th>Payment Method</th>
                  <th>UTR Number</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {loading && transactions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : transactions.length > 0 ? (
                  transactions.map((transaction, index) => (
                    <tr key={transaction._id}>
                      <td>{(page - 1) * limit + index + 1}</td>
                      <td>
                        <div className="d-flex flex-column">
                          <div className="d-flex align-items-center gap-2">
                            <div
                              className="rounded-circle bg-success text-white d-flex align-items-center justify-content-center"
                              style={{
                                width: "35px",
                                height: "35px",
                                fontSize: "14px",
                                fontWeight: "bold"
                              }}
                            >
                              {transaction.userName?.charAt(0) || "U"}
                            </div>
                            <div>
                              <div className="fw-bold">
                                {transaction.userName || "N/A"}
                              </div>
                              {transaction.userMobile && (
                                <small className="text-muted d-block">
                                  <FaMobile className="me-1" size={12} />
                                  {transaction.userMobile}
                                </small>
                              )}
                              {transaction.userEmail && (
                                <small className="text-muted d-block">
                                  <FaEnvelope className="me-1" size={12} />
                                  {transaction.userEmail}
                                </small>
                              )}
                              {transaction.referralCode && (
                                <small className="text-muted d-block">
                                  <FaTag className="me-1" size={12} />
                                  Referral: {transaction.referralCode}
                                </small>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="fw-bold text-success">
                          {formatCurrency(transaction.amount)}
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-info">
                          {transaction.paymentMethod || "N/A"}
                        </span>
                      </td>
                      <td>
                        <code className="bg-light p-1 rounded">
                          {transaction.utrNumber || "N/A"}
                        </code>
                      </td>
                      <td>{getStatusBadge(transaction.status)}</td>
                      <td>
                        <div className="d-flex flex-column">
                          <span>{formatDate(transaction.createdAt)}</span>
                          {transaction.approvedAt && (
                            <small className="text-muted">
                              Approved: {formatDate(transaction.approvedAt)}
                            </small>
                          )}
                          {transaction.remark && (
                            <small className="text-muted">
                              <FaGift className="me-1" size={12} />
                              {transaction.remark}
                            </small>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      {hasActiveFilters ? 'No transactions found matching your filters' : 'No Bonus Transactions Found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
              <button
                className="btn btn-outline-secondary"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1 || loading}
              >
                Previous
              </button>

              <div className="d-flex gap-1">
                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    className={`btn ${pageNum === page ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => goToPage(pageNum)}
                    disabled={loading}
                    style={{ minWidth: '40px' }}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                className="btn btn-outline-secondary"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages || loading}
              >
                Next
              </button>
            </div>
          )}

          <div className="d-flex justify-content-center mt-2">
            <small className="text-muted">
              Showing {transactions.length} of {totalItems} transactions | Page {page} of {totalPages}
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BonusHistoryList;